import { DEG, TAU, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { shatterGlyphs, type Fragment, type ShatterPattern } from '../../core/shatter';
import type { RenderTarget, TechniqueContext } from '../types';
import type { FragmentationValues } from './params';

/**
 * Voronoi fragmentation of the letterforms, without physics.
 *
 * The static counterpart to the shatter technique: the same break patterns, but the
 * pieces are placed by a deterministic transform rather than simulated. That makes it
 * reproducible from the seed alone and instant to render, which is what you want when
 * the goal is a composition rather than a moment of an animation.
 */
export interface PlacedFragment extends Fragment {
  dx: number;
  dy: number;
  rotation: number;
  scale: number;
  tint: number;
  centreDistance: number;
}

export interface FragmentationState {
  fragments: PlacedFragment[];
  palette: ResolvedPalette;
  maxArea: number;
  maxCentreDistance: number;
  built: boolean;
}

function values(ctx: TechniqueContext): FragmentationValues {
  return ctx.values as unknown as FragmentationValues;
}

export function init(ctx: TechniqueContext): FragmentationState {
  const v = values(ctx);
  return {
    fragments: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    maxArea: 1,
    maxCentreDistance: 1,
    built: false,
  };
}

export function step(state: FragmentationState, _dt: number, ctx: TechniqueContext): FragmentationState {
  if (state.built) return state;
  const v = values(ctx);

  const raw = shatterGlyphs(ctx.field.glyphs, v.fragments, v.pattern as ShatterPattern, ctx.rng);
  const bounds = ctx.field.bounds;
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const maxDistance = Math.max(1, Math.hypot(bounds.width, bounds.height) / 2);

  for (const fragment of raw) {
    const dxc = fragment.centroidX - cx;
    const dyc = fragment.centroidY - cy;
    const centreDistance = Math.hypot(dxc, dyc);
    const falloff = v.displacementMode === 'radial' ? 1 - v.distanceFalloff + v.distanceFalloff * (centreDistance / maxDistance) : 1;

    let dx: number;
    let dy: number;
    if (v.displacementMode === 'random') {
      const a = ctx.rng.next() * TAU;
      dx = Math.cos(a) * v.displacement * ctx.rng.next();
      dy = Math.sin(a) * v.displacement * ctx.rng.next();
    } else if (v.displacementMode === 'gravity') {
      dx = ctx.rng.signed(v.displacement * 0.25);
      dy = ctx.rng.next() * v.displacement;
    } else {
      const len = centreDistance || 1;
      dx = (dxc / len) * v.displacement * falloff;
      dy = (dyc / len) * v.displacement * falloff;
    }

    state.fragments.push({
      ...fragment,
      dx,
      dy,
      rotation: ctx.rng.signed(v.rotation * DEG) * falloff,
      scale: 1 + ctx.rng.signed(v.scaleJitter),
      tint: ctx.rng.next(),
      centreDistance,
    });

    if (fragment.area > state.maxArea) state.maxArea = fragment.area;
    if (centreDistance > state.maxCentreDistance) state.maxCentreDistance = centreDistance;
  }

  state.built = true;
  return state;
}

export function isComplete(state: FragmentationState): boolean {
  return state.built;
}

/** Builds the transformed, gap-shrunk polygon for one fragment. */
function fragmentPath(fragment: PlacedFragment, gap: number): Path2D {
  const path = new Path2D();
  const p = fragment.points;
  const n = p.length / 2;
  if (n < 3) return path;

  const cos = Math.cos(fragment.rotation);
  const sin = Math.sin(fragment.rotation);

  for (let i = 0; i < n; i++) {
    let lx = p[i * 2] - fragment.centroidX;
    let ly = p[i * 2 + 1] - fragment.centroidY;

    // Shrink towards the centroid to open the crack lines.
    if (gap > 0) {
      const len = Math.hypot(lx, ly);
      if (len > gap) {
        const k = (len - gap) / len;
        lx *= k;
        ly *= k;
      } else {
        lx = 0;
        ly = 0;
      }
    }
    lx *= fragment.scale;
    ly *= fragment.scale;

    const x = fragment.centroidX + fragment.dx + lx * cos - ly * sin;
    const y = fragment.centroidY + fragment.dy + lx * sin + ly * cos;
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  path.closePath();
  return path;
}

export function draw(state: FragmentationState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  for (const fragment of state.fragments) {
    let t: number;
    switch (v.colorMode) {
      case 'distance':
        t = clamp01(fragment.centreDistance / state.maxCentreDistance);
        break;
      case 'size':
        t = clamp01(fragment.area / state.maxArea);
        break;
      case 'random':
        t = fragment.tint;
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    const path = fragmentPath(fragment, v.gap);

    if (v.style === 'fill' || v.style === 'both') {
      c.fillStyle = color;
      c.fill(path);
    }
    if (v.style === 'stroke' || v.style === 'both') {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: FragmentationState): number {
  return state.fragments.length;
}

export function toSVG(state: FragmentationState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = [`<g stroke-width="${v.lineWidth}" stroke-linejoin="round" fill-opacity="${v.opacity}" stroke-opacity="${v.opacity}">`];

  for (const fragment of state.fragments) {
    let t: number;
    switch (v.colorMode) {
      case 'distance':
        t = clamp01(fragment.centreDistance / state.maxCentreDistance);
        break;
      case 'size':
        t = clamp01(fragment.area / state.maxArea);
        break;
      case 'random':
        t = fragment.tint;
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);

    const p = fragment.points;
    const n = p.length / 2;
    if (n < 3) continue;
    const cos = Math.cos(fragment.rotation);
    const sin = Math.sin(fragment.rotation);
    const pts: string[] = [];
    for (let i = 0; i < n; i++) {
      let lx = p[i * 2] - fragment.centroidX;
      let ly = p[i * 2 + 1] - fragment.centroidY;
      if (v.gap > 0) {
        const len = Math.hypot(lx, ly);
        const k = len > v.gap ? (len - v.gap) / len : 0;
        lx *= k;
        ly *= k;
      }
      lx *= fragment.scale;
      ly *= fragment.scale;
      pts.push(`${f(fragment.centroidX + fragment.dx + lx * cos - ly * sin)},${f(fragment.centroidY + fragment.dy + lx * sin + ly * cos)}`);
    }
    parts.push(
      `<polygon points="${pts.join(' ')}" fill="${v.style === 'stroke' ? 'none' : color}" stroke="${v.style === 'fill' ? 'none' : color}"/>`,
    );
  }

  parts.push('</g>');
  return parts.join('\n');
}
